import type { BagEvent, PairEvent, SpinnerEvent } from '../probabilityEvents.ts';
import type { CounterColour } from '../../../MathFactsGenerator/Constants.ts';

/**
 * Spanish probability prompts (ADR 0108 §2).
 *
 * The letter on a counter is the first letter of the colour's Spanish word, so
 * the key reads naturally. Rojo, azul, verde and amarillo would put A on two
 * counters, so the fourth colour is morado: R, A, V, M. It stands in the slot
 * that is yellow in English and French; nothing on the sheet says "yellow".
 * A counter (ficha) is feminine, so the prompts use roja and morada and the
 * key lists the colour names.
 */
const COLOURS: Record<CounterColour, { letter: string; word: string; adjective: string }> = {
    R: { letter: 'R', word: 'rojo', adjective: 'roja' },
    B: { letter: 'A', word: 'azul', adjective: 'azul' },
    G: { letter: 'V', word: 'verde', adjective: 'verde' },
    Y: { letter: 'M', word: 'morado', adjective: 'morada' },
};

function spinnerEvent(event: SpinnerEvent): string {
    switch (event.kind) {
        case 'number':
            return `obtener un ${event.n}`;
        case 'not':
            return `no obtener un ${event.n}`;
        case 'even':
            return `obtener un número par`;
        case 'odd':
            return `obtener un número impar`;
        case 'greater':
            return `obtener un número mayor que ${event.n}`;
        case 'less':
            return `obtener un número menor que ${event.n}`;
    }
}

function bagEvent(event: BagEvent): string {
    switch (event.kind) {
        case 'colour':
            return `de que sea ${COLOURS[event.colour].adjective}`;
        case 'not':
            return `de que no sea ${COLOURS[event.colour].adjective}`;
        case 'either':
            return `de que sea ${COLOURS[event.colours[0]].adjective} o ${COLOURS[event.colours[1]].adjective}`;
    }
}

function pairEvent(event: PairEvent): string {
    switch (event.kind) {
        case 'both':
            return `obtener un ${event.left} a la izquierda y un ${event.right} a la derecha`;
        case 'bothEven':
            return `obtener dos números pares`;
        case 'same':
            return `obtener el mismo número en las dos`;
    }
}

export const probabilityPrompts = {
    instruction: () => `Escribe cada probabilidad como una fracción en su mínima expresión.`,
    colours: COLOURS,
    counterKey: () =>
        (Object.keys(COLOURS) as CounterColour[]).map((c) => `${COLOURS[c].letter} = ${COLOURS[c].word}`).join(', '),
    oneSpinner: (event: SpinnerEvent) => `Gira la ruleta una vez. ¿Cuál es la probabilidad de ${spinnerEvent(event)}?`,
    bag: (event: BagEvent) => `Saca una ficha sin mirar. ¿Cuál es la probabilidad ${bagEvent(event)}?`,
    twoSpinners: (event: PairEvent) => `Gira las dos ruletas. ¿Cuál es la probabilidad de ${pairEvent(event)}?`,
    outcomes: () => `Gira las dos ruletas. ¿Cuántos resultados diferentes hay?`,
};
